import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useCallback, useState } from 'react';
import { ReportActionSource } from '@amzn/handshake-painter-typescript-client/v1';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { ExternalLink } from 'src/common/externalLink';
import { DogPage } from 'src/common/errors';
import { Loading } from 'src/common/loaders/Loading';
import { StickyContainer, SectionDivider } from 'src/common/styled';
import { ChargeBreakdown, DeliveryDetails, EstimatedDelivery, PaymentDetails, TaxBreakdown, PurchaseButton, } from './components';
import { AddToCartButton } from 'src/common/buttons/addToCart';
import { usePreviewOrder, usePageContext } from 'src/hooks';
import { getExperimentArm } from 'src/utils/experiments';
import { useBagStore, usePaymentStore, useCreatorAttributionStore } from 'src/store';
import { RetailRoutes, useRetailRedirectUrls } from 'src/utils/links';
import { getPreviewOrderState, PreviewOrderErrorType, PreviewOrderStatus, createPreviewOrderRequest } from './utils';
import { useAlert } from 'src/common/alert/AlertContext';
// Helper function since each link shares the same attributes
const formattedLink = (chunk, url, id) => (_jsx(ExternalLink, { id: id, underline: true, href: url, source: ReportActionSource.PreviewOrderPage, children: chunk }, id));
export const PreviewOrderPage = () => {
    var _a, _b, _c;
    const [isPurchaseButtonDisabled] = useState(false);
    const [isCartButtonDisabled] = useState(false);
    const [showDogPage, setShowDogPage] = useState(false);
    const [showBoxShadow, setShowBoxShadow] = useState(false);
    const { clearAlert, showAlert } = useAlert();
    const { bundle, appData } = usePageContext();
    const isVerifyOrderEnabled = getExperimentArm((_a = appData === null || appData === void 0 ? void 0 : appData.data) === null || _a === void 0 ? void 0 : _a.experiments, 'HANDSHAKE_PAINTER_VERIFY_ORDER_TYP') === 'T1';
    /*
     * Get relevant session storage contents for preview order request
     *
     * TODO:
     *  - Move createPreviewOrderRequest into usePreviewOrder, along with usePaymentStore,
     *    useCreatorAttributionStore, useBagStore data
     *  - Graceful handling of preview load without bag contents. May want to add ASIN to
     *    query params in DetailPage and direct them back.
     */
    const { listBag } = useBagStore();
    const { selectedPaymentMethodId } = usePaymentStore();
    const { getCreatorAttributionToken } = useCreatorAttributionStore();
    const bagContents = listBag();
    const pdpAsin = bagContents[0].asin;
    const isPrime = !!((_b = bagContents[0]) === null || _b === void 0 ? void 0 : _b.isPrime);
    const creatorAttributionToken = getCreatorAttributionToken();
    const redirectUrls = useRetailRedirectUrls([
        RetailRoutes.CONDITIONS_OF_USE,
        RetailRoutes.PAYMENT_SELECTION_WALLET,
        RetailRoutes.PRIVACY_NOTICE,
        RetailRoutes.ADDRESS_LIST,
        RetailRoutes.DETAIL_PAGE,
    ], pdpAsin);
    const previewOrderRequest = createPreviewOrderRequest(bagContents, selectedPaymentMethodId, creatorAttributionToken);
    const { data: orderPreview, isLoading, isError, refetch, isRefetching } = usePreviewOrder(previewOrderRequest);
    const errorCode = (_c = orderPreview === null || orderPreview === void 0 ? void 0 : orderPreview.errorReason) === null || _c === void 0 ? void 0 : _c.code;
    const { orderStatus, errorType, translationKey } = getPreviewOrderState(errorCode, isError);
    const renderErrorAlert = useCallback(() => {
        if (orderStatus === PreviewOrderStatus.SUCCESS && !!(orderPreview === null || orderPreview === void 0 ? void 0 : orderPreview.offerDetailsChanged)) {
            showAlert({
                type: 'neutral',
                title: bundle.getMessage('orderUpdateAlertMessage'),
                message: bundle.getMessage(translationKey),
            });
        }
        else if (orderStatus === PreviewOrderStatus.UNFIXABLE_ERROR) {
            showAlert({
                type: 'error',
                message: bundle.formatMessage(translationKey, {
                    a: (chunk) => formattedLink(chunk, redirectUrls.DETAIL_PAGE, 'detail-page-link'),
                }),
            });
        }
        else if (errorType === PreviewOrderErrorType.PAYMENT_METHOD) {
            const message = bundle.formatMessage(translationKey, {
                method: selectedPaymentMethodId ? 'selected' : 'other',
                a: (chunk) => formattedLink(chunk, redirectUrls.PAYMENT_SELECTION_WALLET, 'wallet-link'),
            });
            showAlert({ type: 'warning', message });
        }
        else if (errorType === PreviewOrderErrorType.DELIVERY_ADDRESS) {
            const message = bundle.formatMessage(translationKey, {
                a: (chunk) => formattedLink(chunk, redirectUrls.ADDRESS_LIST, 'address-list-link'),
            });
            showAlert({ type: 'warning', message });
        }
    }, [
        bundle,
        showAlert,
        translationKey,
        errorType,
        orderStatus,
        orderPreview === null || orderPreview === void 0 ? void 0 : orderPreview.offerDetailsChanged,
        selectedPaymentMethodId,
        redirectUrls.ADDRESS_LIST,
        redirectUrls.PAYMENT_SELECTION_WALLET,
        redirectUrls.DETAIL_PAGE,
    ]);
    /**
     * We listen to the window but need to set the `useCapture` option to true
     * https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#usecapture
     *
     * Stack overflow: https://stackoverflow.com/questions/36207398/not-getting-callback-after-adding-an-event-listener-for-scroll-event-in-react-js/65431632
     */
    useEffect(() => {
        const handleScrollEvent = (e) => {
            const target = e.target;
            setShowBoxShadow(target.scrollTop > 48);
        };
        window.addEventListener('scroll', handleScrollEvent, true);
        return () => {
            window.removeEventListener('scroll', handleScrollEvent);
        };
    }, []);
    // Clear the alerts when refetching. Determine which alert to show when not fetching.
    useEffect(() => {
        if (isRefetching) {
            clearAlert();
        }
        else if (!isLoading) {
            renderErrorAlert();
        }
    }, [isRefetching, isLoading, renderErrorAlert, clearAlert]);
    if (isLoading || isRefetching) {
        return _jsx(Loading, {});
    }
    if (isVerifyOrderEnabled && showDogPage) {
        return _jsx(DogPage, {});
    }
    return (_jsxs(Column, { spacing: "none", "data-testid": "preview-order-page", children: [_jsxs(StickyContainer, { spacingInset: "350 350 300 350", spacing: "300", showShadow: showBoxShadow, children: [orderStatus === PreviewOrderStatus.SUCCESS && (_jsx(Text, { "data-testid": "legal-note", type: "b100", children: bundle.formatMessage('legalNote', {
                            conditionsLink: (chunk) => formattedLink(chunk, redirectUrls.CONDITIONS_OF_USE, 'conditions-link'),
                            privacyLink: (chunk) => formattedLink(chunk, redirectUrls.PRIVACY_NOTICE, 'privacy-link'),
                        }) })), _jsxs(Column, { spacing: "300", "data-testid": "cta-block", children: [_jsx(PurchaseButton, { isDisabled: isPurchaseButtonDisabled, previewOrderStatus: orderStatus, refetch: refetch, asin: pdpAsin, purchaseId: orderPreview === null || orderPreview === void 0 ? void 0 : orderPreview.purchaseId, isPrime: isPrime, onGenericError: () => setShowDogPage(true) }), _jsx(AddToCartButton, { isDisabled: isCartButtonDisabled, isPrime: isPrime })] })] }), _jsx(Row, { spacingInset: "none 350 400 350", children: _jsx(Text, { type: "b100", children: bundle.formatMessage('changePaymentMethodAndAddress', {
                        accountLink: (chunk) => formattedLink(chunk, redirectUrls.PAYMENT_SELECTION_WALLET, 'change-payment-method-link'),
                    }) }) }), !isError && (_jsxs(_Fragment, { children: [_jsx(SectionDivider, {}), _jsx(ChargeBreakdown, { charges: orderPreview === null || orderPreview === void 0 ? void 0 : orderPreview.charges })] })), !isError && (orderPreview === null || orderPreview === void 0 ? void 0 : orderPreview.taxSummary) && (_jsxs(_Fragment, { children: [_jsx(SectionDivider, {}), _jsx(TaxBreakdown, { taxSummary: orderPreview.taxSummary })] })), _jsx(SectionDivider, {}), _jsx(PaymentDetails, { errorType: errorType, paymentMethod: orderPreview === null || orderPreview === void 0 ? void 0 : orderPreview.paymentMethod }), _jsx(SectionDivider, {}), _jsx(DeliveryDetails, { errorType: errorType, shippingAddress: orderPreview === null || orderPreview === void 0 ? void 0 : orderPreview.shippingAddress }), _jsx(SectionDivider, {}), _jsx(EstimatedDelivery, { orders: orderPreview === null || orderPreview === void 0 ? void 0 : orderPreview.orders, bagContents: bagContents[0] })] }));
};
